import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Clock, Calendar, Church, UtensilsCrossed, Phone } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../i18n';

const EventDetails = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { t } = useLanguage();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const events = [
    {
      icon: Church,
      title: t('details.ceremonyTitle'),
      time: t('details.ceremonyTime'),
      description: t('details.ceremonyDesc')
    },
    {
      icon: UtensilsCrossed,
      title: t('details.lunchTitle'),
      time: t('details.lunchTime'),
      description: t('details.lunchDesc')
    }
  ];

  const contacts = [
    { name: "Mirko", phone: "3341998209" },
    { name: "Jherli", phone: "3513121048" }
  ];

  return (
    <section 
      ref={sectionRef}
      className="py-28 md:py-40 bg-regency relative overflow-hidden"
      id="dettagli"
    >
      {/* Decorative background */}
      <div className="absolute inset-0 damask-pattern opacity-40" />
      
      {/* Floating decorative elements */}
      <div className="absolute top-20 left-10 w-40 h-40 bg-lavender/10 rounded-full blur-3xl" />
      <div className="absolute bottom-20 right-10 w-48 h-48 bg-blush/25 rounded-full blur-3xl" />
      <div className="absolute top-1/2 left-1/4 w-32 h-32 bg-gold/5 rounded-full blur-3xl" />
      
      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className={`text-center mb-24 transition-all duration-1000 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          {/* Decorative top ornament */}
          <div className="flex items-center justify-center mb-8">
            <svg className="w-16 h-16 text-lavender/40" viewBox="0 0 64 64" fill="currentColor">
              <path d="M32 8 C24 16 8 16 0 24 C8 28 24 28 32 40 C40 28 56 28 64 24 C56 16 40 16 32 8" />
              <circle cx="32" cy="24" r="4" fill="hsl(1 81% 35% / 0.5)" />
            </svg>
          </div>
          
          <p className="font-display text-sm uppercase tracking-[0.5em] text-gold mb-5">
            {t('details.eyebrow')}
          </p>
          <h2 className="font-script text-5xl md:text-6xl lg:text-7xl text-lavender mb-6">
            {t('details.title')}
          </h2>
          <div className="flex items-center justify-center gap-5 mt-10">
            <span className="w-20 h-px bg-gradient-to-r from-transparent to-gold/50" />
            <span className="text-lavender/50">❦</span>
            <span className="w-20 h-px bg-gradient-to-l from-transparent to-gold/50" />
          </div>
        </div>

        {/* Date Card */}
        <Card className={`max-w-2xl mx-auto mb-24 bg-card/95 backdrop-blur-sm border-2 border-gold/25 shadow-elegant transition-all duration-1000 delay-200 gold-frame ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <CardContent className="p-12 md:p-16 text-center relative">
            {/* Inner decorative border */}
            <div className="absolute inset-4 border border-lavender/20 rounded-lg pointer-events-none" />
            
            <div className="flex items-center justify-center gap-3 mb-10">
              <Calendar className="w-6 h-6 text-gold" />
              <span className="font-display text-sm uppercase tracking-[0.4em] text-muted-foreground">
                {t('details.dateLabel')}
              </span>
            </div>
            <p className="font-display text-7xl md:text-8xl text-foreground mb-4">
              28
            </p>
            <p className="font-elegant text-3xl md:text-4xl text-muted-foreground italic">
              {t('date.month')}
            </p>
            <div className="flex items-center justify-center gap-4 my-8">
              <span className="w-12 h-px bg-gold/40" />
              <span className="text-lavender/50">✿</span>
              <span className="w-12 h-px bg-gold/40" />
            </div>
            <p className="font-display text-xl text-lavender tracking-wider">
              {t('date.weekday')}
            </p>
          </CardContent>
        </Card>

        {/* Events Grid */}
        <div className="grid md:grid-cols-2 gap-10 max-w-4xl mx-auto mb-24">
          {events.map((event, index) => (
            <Card 
              key={event.title}
              className={`card-regency bg-card/95 backdrop-blur-sm border-2 border-lavender/20 shadow-romantic transition-all duration-1000 ${
                isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
              }`}
              style={{ transitionDelay: `${300 + index * 150}ms` }}
            >
              <CardContent className="p-12 text-center relative">
                {/* Corner decorations */}
                <div className="absolute top-4 left-4 text-gold/30 text-lg">✿</div>
                <div className="absolute bottom-4 right-4 text-gold/30 text-lg">✿</div>
                
                <div className="w-20 h-20 rounded-full bg-lavender/10 border-2 border-lavender/25 flex items-center justify-center mx-auto mb-10">
                  <event.icon className="w-9 h-9 text-lavender" />
                </div>
                <h3 className="font-script text-3xl md:text-4xl text-lavender mb-4">
                  {event.title}
                </h3>
                <div className="flex items-center justify-center gap-2 mb-6">
                  <Clock className="w-4 h-4 text-gold" />
                  <span className="font-display text-sm text-gold uppercase tracking-wider">
                    {event.time}
                  </span>
                </div>
                <p className="font-elegant text-lg text-muted-foreground leading-relaxed italic">
                  {event.description}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Location Card */}
        <Card className={`max-w-3xl mx-auto mb-24 bg-card/95 backdrop-blur-sm border-2 border-gold/25 shadow-elegant transition-all duration-1000 delay-500 corner-ornate ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <CardContent className="p-12 md:p-16">
            <div className="flex items-center justify-center gap-3 mb-10">
              <MapPin className="w-6 h-6 text-gold" />
              <span className="font-display text-sm uppercase tracking-[0.4em] text-muted-foreground">
                {t('details.location')}
              </span>
            </div>
            <div className="text-center mb-12">
              <h3 className="font-script text-4xl md:text-5xl lg:text-6xl text-lavender mb-5">
                Villa Gazzani
              </h3>
              <div className="flex items-center justify-center gap-4 mb-5">
                <span className="w-10 h-px bg-gold/40" />
                <span className="text-lavender/50">❦</span>
                <span className="w-10 h-px bg-gold/40" />
              </div>
              <p className="font-elegant text-lg text-muted-foreground italic">
                Via Cavallino, 86
              </p>
              <p className="font-elegant text-lg text-muted-foreground italic">
                62012 Civitanova Marche MC
              </p>
            </div>
            
            {/* Map */}
            <div className="relative rounded-xl overflow-hidden border-2 border-lavender/15">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2903.5!2d13.7289!3d43.3089!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x132e37d7e8f75e75%3A0x123456789!2sVia+Cavallino%2C+86%2C+62012+Civitanova+Marche+MC!5e0!3m2!1sit!2sit!4v1620000000000!5m2!1sit!2sit"
                width="100%"
                height="350"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Villa Gazzani Location"
                className="grayscale-[30%] hover:grayscale-0 transition-all duration-500"
              />
            </div>
            
            <div className="text-center mt-10">
              <a 
                href="https://maps.google.com/?q=Via+Cavallino+86+62012+Civitanova+Marche+MC"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 font-display text-sm text-gold hover:text-gold-dark transition-colors group tracking-wider uppercase"
              >
                <MapPin className="w-4 h-4" />
                <span>{t('details.openMaps')}</span>
                <span className="group-hover:translate-x-1 transition-transform">→</span>
              </a>
            </div>
          </CardContent>
        </Card>

        {/* Contacts Card */}
        <Card className={`max-w-2xl mx-auto bg-card/95 backdrop-blur-sm border-2 border-lavender/20 shadow-romantic transition-all duration-1000 delay-600 ${
          isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
        }`}>
          <CardContent className="p-12 md:p-14">
            <div className="flex items-center justify-center gap-3 mb-10">
              <Phone className="w-5 h-5 text-gold" />
              <span className="font-display text-sm uppercase tracking-[0.4em] text-muted-foreground">
                {t('details.contacts')}
              </span>
            </div>
            
            <div className="grid grid-cols-2 gap-10">
              {contacts.map((contact) => (
                <div key={contact.name} className="text-center">
                  <p className="font-script text-3xl text-lavender mb-4">
                    {contact.name}
                  </p>
                  <a 
                    href={`tel:${contact.phone}`}
                    className="font-elegant text-xl text-gold hover:text-gold-dark transition-colors italic"
                  >
                    {contact.phone}
                  </a>
                </div>
              ))}
            </div>
            
            {/* Decorative bottom */}
            <div className="flex items-center justify-center gap-4 mt-12">
              <span className="w-12 h-px bg-lavender/25" />
              <span className="text-gold/50">✿</span>
              <span className="text-lavender/40">❦</span>
              <span className="text-gold/50">✿</span>
              <span className="w-12 h-px bg-lavender/25" />
            </div>
          </CardContent>
        </Card>
      </div>
    </section>
  );
};

export default EventDetails;
